import React from "react";
import { APP_NAME } from "./lib/brand.js";

// The tab reads the app name until a page says otherwise (usePageTitle).
if (typeof document !== "undefined") document.title = APP_NAME;
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth.jsx";
import LoadingSpinner from "./components/LoadingSpinner.jsx";
import { landingFor } from "./lib/roles.js";

// Pages
import LoginPage            from "./pages/LoginPage.jsx";
import EntryFork            from "./pages/EntryFork.jsx";
import FamilySignup         from "./pages/FamilySignup.jsx";
import FamilyClients        from "./pages/family/FamilyClients.jsx";
import ContributePhotos     from "./pages/family/ContributePhotos.jsx";
import ContributeDocuments  from "./pages/family/ContributeDocuments.jsx";
import ContributorHome      from "./pages/ContributorHome.jsx";
import ShareEntry          from "./pages/ShareEntry.jsx";
import ShareBanner         from "./components/ShareBanner.jsx";
import InviteEntry         from "./pages/InviteEntry.jsx";
import InterviewLinkEntry  from "./pages/InterviewLinkEntry.jsx";
import Shell                from "./pages/Shell.jsx";
import MeetPage             from "./pages/MeetPage.jsx";
import SectionPage          from "./pages/SectionPage.jsx";
import PlayThroughPlayer    from "./pages/PlayThroughPlayer.jsx";
import AgencyDashboard      from "./pages/AgencyDashboard.jsx";
import CoordinatorClients   from "./pages/coordinator/CoordinatorClients.jsx";
import ClientEditorHub      from "./pages/coordinator/ClientEditorHub.jsx";
import SectionEditor        from "./pages/coordinator/SectionEditor.jsx";
import QuickStart           from "./pages/coordinator/QuickStart.jsx";
import ReviewGate           from "./pages/coordinator/ReviewGate.jsx";
import PlaythroughScriptEditor from "./pages/coordinator/PlaythroughScriptEditor.jsx";
import MediaLibrary         from "./pages/coordinator/MediaLibrary.jsx";
import InterviewHub         from "./pages/coordinator/InterviewHub.jsx";
import InterviewSetup       from "./pages/coordinator/InterviewSetup.jsx";
import InterviewSession     from "./pages/coordinator/InterviewSession.jsx";
import ImpressionsSession   from "./pages/caregiver/ImpressionsSession.jsx";
import CaregiverClients     from "./pages/caregiver/CaregiverClients.jsx";
import NotFound             from "./pages/NotFound.jsx";

// ── Landing ───────────────────────────────────────────────────────────────────
// Where a signed-in user belongs: lib/roles.js landingFor, which reads the
// server's answer. No component decides it inline.

// ── Protected route ───────────────────────────────────────────────────────────
// Gates on a CAPABILITY rather than a role list (two-customer plan §5):
//
//     <ProtectedRoute capability="build_profiles">   not   roles={["coordinator"]}
//
// The capabilities come from the server on the session user and are ACCOUNT
// level, because a route guard has to decide whether to render before it knows
// which client is involved. Per-client rights are enforced server-side on every
// request the page then makes — this gate is about which surface to show, not
// about who may touch what.
//
// `capability` omitted means "any signed-in user."

function ProtectedRoute({ capability, allowShare = false, children }) {
  const { user, share, loading, can } = useAuth();
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <LoadingSpinner message="Loading…" />
      </div>
    );
  }

  // A share-link visitor reaches a route only when it OPTS IN via allowShare —
  // the three read-only client pages, and nothing else.
  //
  // This used to be "any route that names no capability", which was wrong:
  // /family and /caregiver/clients name no capability either, so a visitor
  // rendered those shells and then hit a wall of 401s that looked like a broken
  // app. Opting in per route makes the set of visitor-reachable pages something
  // you can read off the route table instead of infer.
  if (!user && share) {
    if (!allowShare) return <Navigate to={`/clients/${share.client_id}`} replace />;
    return children;
  }

  if (!user) {
    // Remember where they were going (Plan D: an interview link opened while
    // signed out). Same-origin path only — LoginPage refuses anything else.
    const here = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
    const next = here && here !== "/" ? `?next=${encodeURIComponent(here)}` : "";
    return <Navigate to={`/login${next}`} replace />;
  }
  if (capability && !can(capability)) {
    return <Navigate to={landingFor(user)} replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ShareBanner />
        <Routes>
          {/* Public */}
          <Route path="/"             element={<EntryFork />} />
          <Route path="/login"        element={<LoginPage />} />
          <Route path="/start/family" element={<FamilySignup />} />
          <Route path="/share/:token"  element={<ShareEntry />} />
          <Route path="/invite/:token" element={<InviteEntry />} />
          {/* An interview link: no account (Oct 29). */}
          <Route path="/i/:token" element={<InterviewLinkEntry />} />
          {/* Dev nav index — only in dev builds. In production this route 404s
              (renders NotFound) so the route-enumeration page isn't served to
              scanners. import.meta.env.DEV is Vite's build-time flag. */}
          <Route path="/dev" element={import.meta.env.DEV ? <Shell /> : <NotFound />} />


          {/* Agency admin */}
          <Route path="/agency" element={
            <ProtectedRoute capability="manage_account">
              <AgencyDashboard />
            </ProtectedRoute>
          } />

          {/* Family owner */}
          <Route path="/family" element={
            <ProtectedRoute>
              <FamilyClients />
            </ProtectedRoute>
          } />
          {/* Anyone invited to help, in either kind of account. A family
              Contributor and an agency-invited parent get the same page,
              because they arrived for the same reason. */}
          <Route path="/contribute" element={
            <ProtectedRoute capability="contribute_profiles">
              <ContributorHome />
            </ProtectedRoute>
          } />
          {/* A Contributor's photo surface — deliberately not the coordinator
              media library, which is built around bulk curation and deletion. */}
          <Route path="/family/clients/:clientId/photos" element={
            <ProtectedRoute capability="contribute_profiles">
              <ContributePhotos />
            </ProtectedRoute>
          } />
          {/* A Contributor's documents (Oct 24): upload, and see her own wait
              for the coordinator's review. */}
          <Route path="/family/clients/:clientId/documents" element={
            <ProtectedRoute capability="contribute_profiles">
              <ContributeDocuments />
            </ProtectedRoute>
          } />

          {/* Coordinator */}
          <Route path="/coordinator/clients" element={
            <ProtectedRoute capability="build_profiles">
              <CoordinatorClients />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/edit" element={
            <ProtectedRoute capability="build_profiles">
              <ClientEditorHub />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/edit/:sectionSlug" element={
            <ProtectedRoute capability="build_profiles">
              <SectionEditor />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/quick-start" element={
            <ProtectedRoute capability="build_profiles">
              <QuickStart />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/review" element={
            <ProtectedRoute capability="build_profiles">
              <ReviewGate />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/playthrough-script" element={
            <ProtectedRoute capability="build_profiles">
              <PlaythroughScriptEditor />
            </ProtectedRoute>
          } />
          {/* A section's play-through script (Oct 20): the same editor, its
              slot taken from :sectionSlug. */}
          <Route path="/coordinator/clients/:clientId/edit/:sectionSlug/playthrough-script" element={
            <ProtectedRoute capability="build_profiles">
              <PlaythroughScriptEditor />
            </ProtectedRoute>
          } />
          {/* Interviews are where an invited contributor actually contributes,
              so these gate on contribute_profiles rather than build_profiles.
              Owners and coordinators hold both. Designing objectives (setup)
              stays with builders. */}
          <Route path="/coordinator/clients/:clientId/interviews" element={
            <ProtectedRoute capability="contribute_profiles">
              <InterviewHub />
            </ProtectedRoute>
          } />
          {/* A contributor may start an interview for herself (Oct 24); the
              setup page and the server both keep it to her own. */}
          <Route path="/coordinator/clients/:clientId/interviews/new" element={
            <ProtectedRoute capability="contribute_profiles">
              <InterviewSetup />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/interviews/:interviewId/setup" element={
            <ProtectedRoute capability="build_profiles">
              <InterviewSetup />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/interviews/:interviewId/session" element={
            <ProtectedRoute capability="contribute_profiles">
              <InterviewSession />
            </ProtectedRoute>
          } />
          {/* The link a respondent is handed (Plan D) lives outside
              /coordinator: any signed-in user may open it, and the backend
              decides whether they are the one it was sent to (Plan E2). */}
          <Route path="/clients/:clientId/interviews/:interviewId/session" element={
            <ProtectedRoute>
              <InterviewSession />
            </ProtectedRoute>
          } />
          <Route path="/coordinator/clients/:clientId/media" element={
            <ProtectedRoute capability="build_profiles">
              <MediaLibrary />
            </ProtectedRoute>
          } />

          {/* Caregiver-facing */}
          <Route path="/caregiver/clients" element={
            <ProtectedRoute>
              <CaregiverClients />
            </ProtectedRoute>
          } />
          <Route path="/clients/:clientId" element={
            <ProtectedRoute allowShare>
              <MeetPage />
            </ProtectedRoute>
          } />
          <Route path="/clients/:clientId/playthrough" element={
            <ProtectedRoute allowShare>
              <PlayThroughPlayer />
            </ProtectedRoute>
          } />
          {/* Impressions.

              Placed above the :sectionSlug catch-all for readability, not
              because it must be: react-router v6 ranks routes by specificity
              rather than source order, so a static "impressions" segment beats
              a dynamic one wherever it sits. Order would matter again under a
              first-match router, which is the only reason to keep them
              adjacent.

              What DID matter is that this route existed at all. Without it
              "impressions" was read as a section slug, SectionPage could not
              map it to a section type, and the DSP got "Unknown section:
              impressions".

              ImpressionsSession was written, complete, and never routed — so
              every entry point into it has always landed on that error,
              including the old post-stream modal's primary action. It went
              unnoticed because that modal was dismissible and the feature had
              no other way in.

              No allowShare: a share-link visitor has no User row, and an
              engagement session is recorded against a user. Reflections are
              attributed or they are noise. */}
          <Route path="/clients/:clientId/impressions" element={
            <ProtectedRoute>
              <ImpressionsSession />
            </ProtectedRoute>
          } />
          <Route path="/clients/:clientId/:sectionSlug" element={
            <ProtectedRoute allowShare>
              <SectionPage />
            </ProtectedRoute>
          } />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
